import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { auth, db, rtdb } from "../../firebase";
import ImageViewer from "../ImageViewer";
import { ref, onValue } from "firebase/database";
import { useTheme } from "../../context/ThemeContext"; // 👈 Import Theme
import {
  collection,
  query,
  where,
  onSnapshot,
  doc
} from "firebase/firestore";


export default function ConversationsList({ onSelect, activeId, blockedUsers = [], contacts = {} }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentUid = auth.currentUser?.uid;
  const { isDarkMode } = useTheme(); // 👈 Use Theme

  // 🎨 Dynamic Classes
  const textPrimary = isDarkMode ? "text-white/90" : "text-gray-800";
  const textSecondary = isDarkMode ? "text-white/60" : "text-gray-500";
  const listHover = isDarkMode ? "hover:bg-white/10 hover:border-white/5" : "hover:bg-black/5 hover:border-black/5";
  const listActive = isDarkMode ? "bg-white/20 shadow-lg border-white/10" : "bg-white shadow-md border-gray-200";

  useEffect(() => {
    if (!currentUid) return;

    const q = query(
      collection(db, "conversations"),
      where("members", "array-contains", currentUid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rawConvos = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Sort by updatedAt or createdAt desc
      rawConvos.sort((a, b) => {
        const getMs = (d) => {
          if (!d) return 0;
          if (typeof d.toMillis === 'function') return d.toMillis();
          if (d instanceof Date) return d.getTime();
          if (d.seconds) return d.seconds * 1000;
          return 0;
        };
        const tA = getMs(a.lastMessageTimestamp) || getMs(a.updatedAt) || getMs(a.createdAt);
        const tB = getMs(b.lastMessageTimestamp) || getMs(b.updatedAt) || getMs(b.createdAt);
        return tB - tA;
      });

      setConversations(rawConvos);
      setLoading(false);
    }, (error) => {
      console.error("Conversations list load error:", error);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUid]);

  return (
    <div className="p-4 h-full">
      <h2 className={`text-2xl font-bold mb-4 tracking-tight ${isDarkMode ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400' : 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600'}`}>Chats</h2>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex justify-center mt-10"
          >
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDarkMode ? 'border-white' : 'border-blue-500'}`}></div>
          </motion.div>
        ) : conversations.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-center mt-10 text-sm ${textSecondary}`}
          >
            No conversations yet. <br /> Start a new chat!
          </motion.div>
        ) : (
          <motion.ul
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ staggerChildren: 0.1 }}
            className="space-y-2"
          >
            {conversations.filter(c => {
              const otherUid = c.members.find(m => m !== currentUid);
              return !blockedUsers || !blockedUsers.includes(otherUid);
            }).map((c) => (
              <ConversationItem
                key={c.id}
                conversation={c}
                currentUid={currentUid}
                onSelect={onSelect}
                isActive={activeId === c.id}
                isDarkMode={isDarkMode}
                classes={{ textPrimary, textSecondary, listHover, listActive }}
                contacts={contacts}
              />
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

// 🟢 Helper to format time (e.g., 10:30 AM or Yesterday)
const formatTime = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const isToday = date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString();
};

// 🟢 Sub-component for individual list items
// 🟢 Sub-component for individual list items
function ConversationItem({ conversation, currentUid, onSelect, isActive, isDarkMode, classes, contacts }) {
  const [otherUser, setOtherUser] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [viewImage, setViewImage] = useState(null);

  const otherUid = conversation.members.find((uid) => uid !== currentUid);

  // Display Name Logic (Alias > Username)
  const displayName = contacts?.[otherUid] || otherUser?.username || "Unknown";

  useEffect(() => {
    if (!otherUid) return;

    // 1. Fetch User Data (Firestore)
    const unsubUser = onSnapshot(doc(db, "users", otherUid), (docSnap) => {
      if (docSnap.exists()) {
        setOtherUser({ uid: otherUid, ...docSnap.data() });
      }
    });

    // 2. Fetch Online Status (RTDB)
    const statusRef = ref(rtdb, `/status/${otherUid}`);
    const unsubStatus = onValue(statusRef, (snapshot) => {
      const statusData = snapshot.val();
      setIsOnline(statusData?.state === "online");
    });

    // 3. Fetch Typing Status (RTDB)
    const typingRef = ref(rtdb, `/typing/${conversation.id}/${otherUid}`);
    const unsubTyping = onValue(typingRef, (snapshot) => {
      setIsTyping(Boolean(snapshot.val()));
    });

    return () => {
      unsubUser();
      unsubStatus();
      unsubTyping();
    };
  }, [otherUid, conversation.id]);

  if (!otherUser) return null;

  // Last Message Logic
  const lastMsg = conversation.lastMessage || "Start a conversation";
  const lastTime = formatTime(conversation.lastMessageTimestamp || conversation.updatedAt || conversation.createdAt);
  const isMe = conversation.lastMessageSenderId === currentUid;

  // Item Text Colors
  const nameColor = isDarkMode ? "text-white" : "text-gray-900";
  const msgColor = isDarkMode ? "text-gray-200" : "text-gray-600";
  const timeColor = isDarkMode ? "text-gray-300" : "text-gray-500";

  return (
    <>
      <li
        className={`p-3 rounded-xl cursor-pointer transition-all border border-transparent 
        ${isActive ? classes.listActive : classes.listHover}`}
        onClick={() =>
          onSelect({
            convId: conversation.id,
            uid: otherUser.uid,
            username: displayName,
            originalUsername: otherUser.username,
            photoURL: otherUser.photoURL,
          })
        }
      >
        <div className="flex items-center gap-3 relative">
          <div className="relative">
            {otherUser.photoURL ? (
              <img
                src={otherUser.photoURL}
                alt={displayName}
                className={`w-12 h-12 rounded-full object-cover shadow-md border hover:scale-105 transition-transform cursor-zoom-in ${isDarkMode ? 'border-white/20' : 'border-gray-200'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setViewImage(otherUser.photoURL);
                }}
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold shadow-md text-lg">
                {displayName?.[0]?.toUpperCase()}
              </div>
            )}
            {/* 🟢/🔴 Online Status Badge */}
            {otherUser.showLastSeen !== false && (
              <span className={`absolute bottom-0 right-0 w-3.5 h-3.5 border-2 rounded-full shadow-sm z-10 ${isOnline ? 'bg-green-500' : 'bg-red-500'} ${isDarkMode ? 'border-[#111b21]' : 'border-white'}`}></span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-center mb-0.5">
              <p className={`font-semibold truncate text-base ${nameColor}`}>{displayName}</p>
              <span className={`text-[10px] font-medium whitespace-nowrap ml-2 opacity-80 ${timeColor}`}>{lastTime}</span>
            </div>
            <p className={`text-sm truncate pr-2 opacity-90 flex justify-between items-center ${msgColor}`}>
              <span className="truncate block flex-1 h-5">
                {isTyping ? (
                  <span className="text-[#00a884] font-medium text-xs animate-pulse flex items-center gap-1">
                    Typing<span className="animate-bounce">.</span><span className="animate-bounce delay-100">.</span><span className="animate-bounce delay-200">.</span>
                  </span>
                ) : (
                  <>
                    {isMe && <span className="mr-1 inline-block">✓</span>}
                    {lastMsg}
                  </>
                )}
              </span>
              {conversation.unreadCounts?.[currentUid] > 0 && (
                <span className="ml-2 bg-[#25d366] text-white text-[10px] font-bold h-5 min-w-[1.25rem] px-1.5 flex items-center justify-center rounded-full shadow-sm">
                  {conversation.unreadCounts[currentUid]}
                </span>
              )}
            </p>
          </div>
        </div>
      </li>
      {/* 🖼 Image Viewer Modal */}
      {viewImage && (
        <ImageViewer
          src={viewImage}
          alt={displayName}
          onClose={() => setViewImage(null)}
        />
      )}
    </>
  );
}
